import "@/design/tokens.css";
import "@/App.css";
import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import GlassDefs from "@/design/materials/GlassDefs";

const Landing = lazy(() => import("@/pages/Landing"));
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Builder = lazy(() => import("@/pages/Builder"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Templates = lazy(() => import("@/pages/Templates"));
const Security = lazy(() => import("@/pages/Security"));
const Status = lazy(() => import("@/pages/Status"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const BillingHistory = lazy(() => import("@/pages/BillingHistory"));
const EmailSettings = lazy(() => import("@/pages/EmailSettings"));
const Admin = lazy(() => import("@/pages/Admin"));
const BillingSuccess = lazy(() => import("@/pages/Billing").then((m) => ({ default: m.BillingSuccess })));
const BillingError = lazy(() => import("@/pages/Billing").then((m) => ({ default: m.BillingError })));
const RefundPolicy = lazy(() => import("@/pages/RefundPolicy").then((m) => ({ default: m.RefundPolicy })));

function RouteFallback() {
  return <div style={{ minHeight: "100vh", background: "var(--void)" }} />;
}

/**
 * Guards the lazy route tree. Route splitting (Task 14) means a rejected
 * dynamic import() — e.g. a visitor holding stale HTML after a deploy
 * re-hashes chunk filenames — unmounts the whole React tree with nothing
 * to catch it. First failure: reload once, guarded by a sessionStorage
 * flag so a persistently broken chunk cannot loop forever. Second failure
 * (post-reload): render a minimal panel with a manual retry link instead
 * of a blank page.
 */
class ChunkErrorBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    if (!sessionStorage.getItem("f6.chunkReloaded")) {
      sessionStorage.setItem("f6.chunkReloaded", "1");
      window.location.reload();
    }
  }

  render() {
    if (this.state.failed) {
      return (
        <div
          style={{
            minHeight: "100vh",
            background: "var(--void)",
            color: "var(--text, #fff)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            padding: "2rem",
            textAlign: "center",
            fontFamily: "system-ui, sans-serif",
          }}
        >
          <p>Something didn&apos;t load correctly.</p>
          <a href="/" style={{ color: "var(--brand-ink, #CCFF00)" }}>
            Reload the page
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    /* `theme-liquid` sits on the router root so ONE palette covers the whole
       product. It used to be per-page (Landing/Pricing/Signup only), which
       meant a visitor signed up on a light page and landed on a dark
       Dashboard — it read as a different product. */
    <div className="App theme-liquid">
      <GlassDefs />
      <AuthProvider>
        <BrowserRouter>
          <ChunkErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                {/* Deliberately NOT wrapped in ProtectedRoute: that component
                    redirects unverified users here, so wrapping this route
                    would redirect it to itself forever. */}
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/templates" element={<Templates />} />
                <Route path="/security" element={<Security />} />
                <Route path="/status" element={<Status />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/billing/success" element={<BillingSuccess />} />
                <Route path="/billing/error" element={<BillingError />} />
                <Route path="/billing/history" element={<ProtectedRoute><BillingHistory /></ProtectedRoute>} />
                <Route path="/settings/email" element={<ProtectedRoute><EmailSettings /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route
                  path="/dashboard"
                  element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
                />
                <Route
                  path="/builder/:projectId"
                  element={<ProtectedRoute><Builder /></ProtectedRoute>}
                />
              </Routes>
            </Suspense>
          </ChunkErrorBoundary>
          {/* Toasts are the one surface that floats over every page, so a dark
              one would be the last black rectangle in a light product. Tokens
              rather than hexes, so it cannot drift from the theme again. */}
          <Toaster
            theme="light" position="top-right"
            toastOptions={{
              style: {
                background: "var(--surface)", border: "1px solid var(--hairline)",
                color: "var(--text)", borderRadius: "12px",
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                fontSize: "13px", boxShadow: "0 8px 30px rgba(11,13,23,0.12)",
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export { ChunkErrorBoundary };
export default App;
